import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, XCircle, Clock, ExternalLink, Loader2, History } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type Application = {
  id: string;
  org_name: string;
  contact_name: string;
  contact_email: string;
  website: string | null;
  tier_requested: "advocate" | "partner" | "anchor";
  services: string[];
  counties: string[];
  why: string | null;
  status: "pending" | "approved" | "rejected";
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
};

type Event = {
  id: string;
  application_id: string;
  actor_user_id: string | null;
  from_status: string | null;
  to_status: string;
  note: string | null;
  created_at: string;
};

const STATUS_FILTERS = ["pending", "approved", "rejected", "all"] as const;
type Filter = (typeof STATUS_FILTERS)[number];

const tierBadge = (tier: Application["tier_requested"]) => {
  const map = {
    advocate: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    partner: "bg-primary/10 text-primary border-primary/30",
    anchor: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  };
  return map[tier];
};

const statusIcon = (status: string) =>
  status === "approved" ? (
    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
  ) : status === "rejected" ? (
    <XCircle className="h-3.5 w-3.5 text-destructive" />
  ) : (
    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
  );

export default function PartnerApplicationsTab() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>("pending");
  const [selected, setSelected] = useState<Application | null>(null);
  const [actionOpen, setActionOpen] = useState<null | "approve" | "reject">(null);
  const [reviewNote, setReviewNote] = useState("");

  const appsQuery = useQuery({
    queryKey: ["partner-applications", filter],
    queryFn: async (): Promise<Application[]> => {
      let q = supabase
        .from("partner_applications")
        .select("*")
        .order("created_at", { ascending: false });
      if (filter !== "all") q = q.eq("status", filter);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Application[];
    },
  });

  const eventsQuery = useQuery({
    enabled: !!selected,
    queryKey: ["partner-application-events", selected?.id],
    queryFn: async (): Promise<Event[]> => {
      const { data, error } = await supabase
        .from("partner_application_events")
        .select("*")
        .eq("application_id", selected!.id)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Event[];
    },
  });

  const reviewMutation = useMutation({
    mutationFn: async (vars: { id: string; status: "approved" | "rejected"; note: string }) => {
      // Guard: do NOT email anyone whose contact_email already belongs to an
      // existing affiliate/partner. Tests run only against brand-new applicants
      // until the flow is signed off end-to-end.
      const app = selected;
      let shouldEmail = false;
      let referralCode: string | null = null;

      if (app) {
        const { data: existing, error: exErr } = await supabase
          .from("affiliates")
          .select("id")
          .eq("contact_email", app.contact_email)
          .limit(1);
        if (exErr) throw exErr;
        shouldEmail = (existing?.length ?? 0) === 0;
      }

      if (vars.status === "approved") {
        // Server-side: marks approved, creates affiliate org, grants role, logs event.
        const { data, error } = await supabase.rpc("approve_partner_application", {
          _app_id: vars.id,
          _note: vars.note.trim() || null,
        });
        if (error) throw error;
        referralCode =
          (data as { referral_code?: string } | null)?.referral_code ?? null;
      } else {
        // Reject path: update + reviewer note
        const { error: upErr } = await supabase
          .from("partner_applications")
          .update({
            status: vars.status,
            reviewed_by: user?.id ?? null,
            reviewed_at: new Date().toISOString(),
          })
          .eq("id", vars.id);
        if (upErr) throw upErr;

        if (vars.note.trim()) {
          const { error: evErr } = await supabase.from("partner_application_events").insert({
            application_id: vars.id,
            actor_user_id: user?.id ?? null,
            from_status: "pending",
            to_status: vars.status,
            note: vars.note.trim(),
          });
          if (evErr) throw evErr;
        }
      }

      // Outbound email — only for brand-new applicants during the test window
      if (app && shouldEmail) {
        const templateName =
          vars.status === "approved"
            ? "partner-application-approved"
            : "partner-application-rejected";
        const templateData =
          vars.status === "approved"
            ? {
                name: app.contact_name,
                orgName: app.org_name,
                tier: app.tier_requested,
                referralCode,
                handbookUrl: "https://emrkt.co/partners/handbook",
              }
            : {
                name: app.contact_name,
                orgName: app.org_name,
                tier: app.tier_requested,
                reviewerNote: vars.note.trim() || null,
              };
        const { error: mailErr } = await supabase.functions.invoke(
          "send-transactional-email",
          {
            body: {
              templateName,
              recipientEmail: app.contact_email,
              idempotencyKey: `partner-app-${vars.status}-${vars.id}`,
              templateData,
            },
          },
        );
        if (mailErr) {
          // Don't fail the whole review just because the email errored.
          console.error("Partner application email send failed", mailErr);
        }
      }

      return { emailed: shouldEmail };
    },
    onSuccess: (data, vars) => {
      const emailed = (data as { emailed?: boolean } | undefined)?.emailed;
      toast.success(
        vars.status === "approved"
          ? emailed
            ? "Approved — provisioned, role granted, welcome email sent"
            : "Approved — provisioned & role granted (email skipped: existing affiliate)"
          : emailed
            ? "Application rejected — notice email sent"
            : "Application rejected (email skipped: existing affiliate)",
      );
      qc.invalidateQueries({ queryKey: ["partner-applications"] });
      qc.invalidateQueries({ queryKey: ["partner-application-events"] });
      setActionOpen(null);
      setReviewNote("");
      setSelected(null);
    },
    onError: (e: Error) => toast.error("Review failed", { description: e.message }),
  });

  const counts = appsQuery.data?.reduce(
    (acc, a) => {
      acc[a.status] = (acc[a.status] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold">Partner Applications</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Review incoming program applications. Approving here marks the application — provision the
            partner org in the Affiliates tab once approved.
          </p>
        </div>
        <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
          <TabsList>
            {STATUS_FILTERS.map((s) => (
              <TabsTrigger key={s} value={s} className="capitalize">
                {s}
                {counts && s !== "all" && counts[s] ? (
                  <span className="ml-1.5 text-xs text-muted-foreground">{counts[s]}</span>
                ) : null}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {appsQuery.isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading applications…
        </div>
      ) : (appsQuery.data?.length ?? 0) === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">
          No applications in this view.
        </Card>
      ) : (
        <div className="grid gap-3">
          {appsQuery.data!.map((a) => (
            <Card
              key={a.id}
              className="p-4 cursor-pointer hover:border-primary/50 transition"
              onClick={() => setSelected(a)}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <span className="font-semibold text-foreground">{a.org_name}</span>
                    <Badge variant="outline" className={tierBadge(a.tier_requested)}>
                      {a.tier_requested}
                    </Badge>
                    <Badge variant="outline" className="gap-1.5">
                      {statusIcon(a.status)}
                      <span className="capitalize">{a.status}</span>
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {a.contact_name} · {a.contact_email}
                  </div>
                  {a.services.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {a.services.slice(0, 6).map((s) => (
                        <span key={s} className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
                          {s}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {new Date(a.created_at).toLocaleDateString()}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selected.org_name}
                  <Badge variant="outline" className={tierBadge(selected.tier_requested)}>
                    {selected.tier_requested}
                  </Badge>
                </DialogTitle>
                <DialogDescription>
                  Submitted {new Date(selected.created_at).toLocaleString()}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-muted-foreground">Contact</div>
                    <div>{selected.contact_name}</div>
                    <a className="text-primary hover:underline" href={`mailto:${selected.contact_email}`}>
                      {selected.contact_email}
                    </a>
                  </div>
                  {selected.website && (
                    <div>
                      <div className="text-xs text-muted-foreground">Website</div>
                      <a
                        href={selected.website}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:underline inline-flex items-center gap-1"
                      >
                        {selected.website} <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}
                </div>

                {selected.services.length > 0 && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Services</div>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.services.map((s) => (
                        <Badge key={s} variant="secondary">{s}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selected.counties.length > 0 && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Counties served</div>
                    <div className="text-foreground">{selected.counties.join(", ")}</div>
                  </div>
                )}

                {selected.why && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Why join</div>
                    <p className="text-foreground/90 whitespace-pre-wrap">{selected.why}</p>
                  </div>
                )}

                {/* Status history */}
                <div className="pt-3 border-t border-border">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                    <History className="h-3.5 w-3.5" /> Status history
                  </div>
                  {eventsQuery.isLoading ? (
                    <div className="text-muted-foreground">Loading…</div>
                  ) : (
                    <ol className="space-y-2">
                      {(eventsQuery.data ?? []).map((ev) => (
                        <li key={ev.id} className="flex gap-3">
                          <div className="mt-0.5">{statusIcon(ev.to_status)}</div>
                          <div className="flex-1">
                            <div className="text-foreground capitalize">
                              {ev.from_status ? `${ev.from_status} → ${ev.to_status}` : ev.to_status}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {new Date(ev.created_at).toLocaleString()}
                            </div>
                            {ev.note && <div className="text-sm mt-1 text-foreground/80">{ev.note}</div>}
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </div>

              <DialogFooter className="gap-2">
                {selected.status === "pending" ? (
                  <>
                    <Button
                      variant="outline"
                      className="text-destructive border-destructive/40 hover:bg-destructive/10"
                      onClick={() => setActionOpen("reject")}
                    >
                      <XCircle className="h-4 w-4 mr-1.5" /> Reject
                    </Button>
                    <Button onClick={() => setActionOpen("approve")}>
                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Review action dialog */}
      <Dialog open={!!actionOpen} onOpenChange={(o) => !o && setActionOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionOpen === "approve" ? "Approve application" : "Reject application"}
            </DialogTitle>
            <DialogDescription>
              {actionOpen === "approve"
                ? "Approving will create the partner directory entry, generate a referral code, and grant the applicant their tier role. Partner & Anchor tiers publish to the directory immediately."
                : "Add a brief reason. It will be saved in the application's status history."}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="rn">Note {actionOpen === "reject" && <span className="text-destructive">*</span>}</Label>
            <Textarea
              id="rn"
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
              className="mt-1.5 min-h-[100px]"
              placeholder={
                actionOpen === "approve"
                  ? "Optional: account manager, special terms, onboarding notes…"
                  : "Reason — e.g. out of scope, insufficient AL presence, missing references."
              }
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionOpen(null)}>Cancel</Button>
            <Button
              variant={actionOpen === "reject" ? "destructive" : "default"}
              disabled={
                reviewMutation.isPending ||
                (actionOpen === "reject" && !reviewNote.trim()) ||
                !selected
              }
              onClick={() =>
                selected &&
                actionOpen &&
                reviewMutation.mutate({
                  id: selected.id,
                  status: actionOpen === "approve" ? "approved" : "rejected",
                  note: reviewNote,
                })
              }
            >
              {reviewMutation.isPending ? "Saving…" : actionOpen === "approve" ? "Confirm approve" : "Confirm reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}